import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import qs from 'qs';

import { Loader } from 'ui';

import { CompanyPanel } from 'ui/Company';
import { RoundButton, DownloadButton } from 'ui/Buttons';
import { usePortfolioModals, useAccess } from 'shared/hooks';

import { lastVisitedRatingTypeAction } from 'store/shared/lastVisited/lastVisitedActions';
import { fetchEntities, requestCreditReviewRatings } from 'services';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { getCurrentEnvironmentModules, companyEntityTabs } from 'shared/constants/company';
import { getName } from 'country-list';
import { EnvironmentTypes } from 'shared/constants/environmentTypes';
import { moduleTypes } from 'shared/constants/moduleTypes';
import { CreditReview } from 'modules/creditReview';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { ItemType } from 'shared/types/portfolios';

const EntityDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { easyNumber } = useParams();
  const dispatch = useDispatch();
  const { userId, organisationId } = useSelector(({ auth }) => auth.data);
  const isTestingEnvironment = useAccess({ environment: EnvironmentTypes.TESTING });
  const currentEnvironmentModules = getCurrentEnvironmentModules(isTestingEnvironment);

  const [isPending, setIsPending] = useState(false);
  const [entity, setEntity] = useState(null);

  const [requested, setRequested] = useState(false);
  const [ratingAvailable, setRatingAvailable] = useState(false);
  const [isRequestRatingPending, setIsRequestRatingPending] = useState(false);

  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const country = entity ? entity.countryCode || entity.country : '';

  const modules = currentEnvironmentModules.find(
    ({ value }) => value === moduleTypes.CREDIT_REVIEW,
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsPending(true);

      const { data } = await fetchEntities(
        {
          page: 1,
          limit: 1,
          query: easyNumber, // TODO: change to { filter: { easyNumber } } when backend is ready
          userId,
        },
        ratingTypes.CREDIT_REVIEW,
      );

      if (data && data.length) {
        const entityData = data[0];

        setEntity(entityData);
        setRatingAvailable(!!entityData.requested);
      }

      setIsPending(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);

  const [activeTabs, setActiveTabs] = useState({
    companyModule: currentEnvironmentModules.find(
      ({ value }) => value === moduleTypes.CREDIT_REVIEW,
    ),
  });

  useEffect(() => {
    dispatch(lastVisitedRatingTypeAction({ ratingType: activeTabs.companyModule }));
  }, [activeTabs.companyModule]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { companyTab, companyModule } = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (companyEntityTabs.map(({ value }) => value).includes(companyTab)) {
      setActiveTabs((currentValues) => ({ ...currentValues, companyTab }));
    }

    if (currentEnvironmentModules.map(({ value }) => value).includes(companyModule)) {
      setActiveTabs((currentValues) => ({ ...currentValues, companyModule }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemsForPortfolio = {
    items: { [easyNumber]: easyNumber },
    count: 1,
    type: ItemType.EASYNUMBER,
  };

  const panelData = () => [
    {
      label: t('Industry'),
      value: entity.sector || '-',
    },
    {
      label: t('Country'),
      value: getName(country) || country,
    },
    {
      label: t('Instruments'),
      value: '-',
    },
  ];

  const handleRatingRequest = async () => {
    setIsRequestRatingPending(true);

    const response = await requestCreditReviewRatings({
      ids: [easyNumber],
      userId,
      organisationId,
    });

    if (response.success) {
      setRequested(true);
      setRatingAvailable(true);
      setIsRequestRatingPending(false);
    }
  };

  if (!entity) return <Loader />;

  return (
    <>
      <CompanyPanel
        loading={isPending}
        title={entity.name || entity.entityName}
        action={
          <>
            {entity.is_sample && (
              <DownloadButton
                url={`/sig/generate-report/${easyNumber}`}
                directDownload
                fileName={`credit review report - ${easyNumber}`}
              >
                {t('PDF')}
              </DownloadButton>
            )}
            <RoundButton label={t('Add to portfolio')} onClick={addToPortfolio.show} marginLeft />
          </>
        }
        items={entity ? panelData() : []}
        activeModule={activeTabs.companyModule}
        onChangeModule={() => {}}
        modules={[modules]}
      />
      {currentEnvironmentModules.find(({ value }) => value === moduleTypes.CREDIT_REVIEW).value ===
        activeTabs.companyModule && (
        <CreditReview
          ratingAvailable={ratingAvailable}
          entity={entity}
          easyNumber={easyNumber}
          isPending={isPending}
          isRequestRatingPending={isRequestRatingPending}
          handleRatingRequest={handleRatingRequest}
        />
      )}
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        itemsData={itemsForPortfolio}
        itemType="ENTITY"
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={itemsForPortfolio}
        itemType="ENTITY"
      />
    </>
  );
};

export { EntityDetails };
